import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Modal from "components/ui/modals";
import {
  useCreateVoucher,
  useOneVoucher,
  useUpdateVoucher,
  useNextVoucherNumber,
  useOutstandingBills,
} from "http-hooks/voucher/useVoucher";
import { useUploadDocument } from "http-hooks/document/useDocument";
import { useAllBanks } from "http-hooks/bank/useBank";
import { useAllBankPayTps } from "http-hooks/bankpaytp/useBankPayTp";
import Input from "components/ui/Input";
import Select from "components/ui/Select";
import TextArea from "components/ui/TextArea";
import FileUploader from "components/ui/FileUploader";
import SelectVendors from "components/ui/selectors/SelectVendors";
import SelectCustomers from "components/ui/selectors/SelectCustomers";
import useCompanyPreferences from "hooks/useCompanyPreferences";

// Add this loading skeleton component
const FormSkeleton = () => (
  <div className="animate-pulse">
    <div className="h-7 bg-gray-200 rounded w-1/4 mb-8"></div>
    <div className="space-y-6">
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-9 bg-gray-200 rounded w-full"></div>
      </div>
      <div>
        <div className="h-4 bg-gray-200 rounded w-1/4 mb-2"></div>
        <div className="h-6 bg-gray-200 rounded w-12"></div>
      </div>
    </div>
  </div>
);

const cndnNoteOptions = [
  { value: "", label: "None" },
  { value: "VCN", label: "Credit Note" },
  { value: "VDN", label: "Debit Note" },
];

const RedirectOverlay = ({ noteType }) => {
  const label = noteType === "VCN" ? "Credit Note" : "Debit Note";
  return (
    <div className="fixed inset-0 z-[9999] flex flex-col items-center justify-center bg-gray-900">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-white border-t-transparent mb-6" />
      <h2 className="text-2xl font-semibold text-white mb-2">
        Money Receipt Saved
      </h2>
      <p className="text-lg text-gray-300">
        Redirecting to {label}...
      </p>
    </div>
  );
};

// Outstanding Bills Modal Component
const OutstandingBillsModal = ({ isOpen, onClose, vendorId, customerId, onBillsSelected }) => {
  const [selectedBills, setSelectedBills] = useState([]);

  const { data: bills = [], isLoading: loading } = useOutstandingBills(
    vendorId,
    customerId,
    isOpen
  );

  // Reset selected bills when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedBills([]);
    }
  }, [isOpen]);

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}/${month}/${year}`;
  };

  const handleBillToggle = (bill) => {
    setSelectedBills(prev => {
      const exists = prev.find(b => b.id === bill.id);
      if (exists) {
        return prev.filter(b => b.id !== bill.id);
      } else {
        return [...prev, bill];
      }
    });
  };

  const handleConfirm = () => {
    const totalAmount = selectedBills.reduce((sum, bill) => sum + parseFloat(bill.amount), 0);
    const narration = "Against doc no. - " + selectedBills.map(b => b.docno).join(", ");
    onBillsSelected(totalAmount, narration);
    setSelectedBills([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[600px] flex flex-col">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold">List of Outstanding Bills</h3>
          </div>
          
          <div className="flex-1 overflow-y-auto px-6 py-4">
            {loading ? (
              <div>Loading...</div>
            ) : bills.length === 0 ? (
              <div className="text-center text-gray-500">No outstanding bills found</div>
            ) : (
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Select</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Doc Type</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Doc No</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {bills.map(bill => (
                    <tr key={bill.id}>
                      <td className="px-3 py-2">
                        <Input
                          type="checkbox"
                          showLabel={false}
                          value={selectedBills.some(b => b.id === bill.id)}
                          onChange={() => handleBillToggle(bill)}
                        />
                      </td>
                      <td className="px-3 py-2 text-sm">{bill.doctype}</td>
                      <td className="px-3 py-2 text-sm">{bill.docno}</td>
                      <td className="px-3 py-2 text-sm">{formatDate(bill.date)}</td>
                      <td className="px-3 py-2 text-sm text-right">{parseFloat(bill.amount).toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
          
          <div className="px-6 py-4 border-t border-gray-200 flex justify-between items-center">
            <div className="font-bold">
              Total: Rs. {selectedBills.reduce((sum, b) => sum + parseFloat(b.amount), 0).toFixed(2)}
            </div>
            <div className="space-x-3">
              <button
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirm}
                disabled={selectedBills.length === 0}
                className="px-4 py-2 bg-indigo-600 border border-transparent rounded-md text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default function SetMoneyReceipt({ id, onClose, open }) {
  const navigate = useNavigate();
  const [selectedDebitAccount, setSelectedDebitAccount] = useState(null);
  const [selectedCreditAccount, setSelectedCreditAccount] = useState(null);
  const [selectedBank, setSelectedBank] = useState(null);
  const [selectedBankPayTp, setSelectedBankPayTp] = useState(null);
  const [paymentMode, setPaymentMode] = useState("B");
  const [selectionType, setSelectionType] = useState("ACC");
  const [showCustomMR, setShowCustomMR] = useState(false);
  const [showBillsModal, setShowBillsModal] = useState(false);
  const [cndnNoteType, setCndnNoteType] = useState("");
  const [showRedirectOverlay, setShowRedirectOverlay] = useState(false);
  const [attachment, setAttachment] = useState(null);

  const { isLoading, data } = useOneVoucher(id);
  const { data: voucherNumberData } = useNextVoucherNumber("MR", !id);
  const { isDocUploaderEnabled } = useCompanyPreferences();

  const [voucherDetails, setVoucherDetails] = useState({
    docno: "",
    custom_mrno: "",
    trdate: "",
    trtyp: "MR",
    acdebit: "",
    accredit: "",
    tramount: "0.00",
    naration: "",
    bankname: "",
    docmode: "",
    trnid: "",
    trndt: "",
    is_revised: 0,
  });

  const { data: allBanksData, isLoading: isLoadingAllBanks } = useAllBanks();
  const allBanks = useMemo(() => allBanksData?.data || [], [allBanksData]);

  const { data: allBankPayTpsData, isLoading: isLoadingAllBankPayTps } = useAllBankPayTps();
  const allBankPayTps = useMemo(() => allBankPayTpsData?.data || [], [allBankPayTpsData]);

  const docFilename = useMemo(() => {
    const mrNo = showCustomMR && voucherDetails.custom_mrno
      ? voucherDetails.custom_mrno
      : voucherDetails.docno;
    if (!mrNo) return "";
    return `MR-${mrNo}`;
  }, [showCustomMR, voucherDetails.custom_mrno, voucherDetails.docno]);

  const { mutateAsync: createMutateAsync, isPending: isCreating } = useCreateVoucher();

  const { mutateAsync: updateMutateAsync, isPending: isUpdating } = useUpdateVoucher();
  const { mutateAsync: uploadDocument, isPending: isUploading } = useUploadDocument();

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setVoucherDetails((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSelectionTypeChange = (type) => {
    setSelectionType(type);
    if (type === "ACC") {
      setVoucherDetails(prev => ({
        ...prev,
        naration: "As On Account"
      }));
    } else if (type === "BILL") {
      if (selectedDebitAccount && selectedCreditAccount) {
        setShowBillsModal(true);
      }
    }
  };

  const handleBillsSelected = (totalAmount, narration) => {
    setVoucherDetails(prev => ({
      ...prev,
      tramount: totalAmount.toFixed(2),
      naration: narration
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Custom MR numbers are stored exactly as entered, matching the PHP behavior.
    const finalDocNo = showCustomMR && voucherDetails.custom_mrno
      ? voucherDetails.custom_mrno
      : voucherDetails.docno;

    const formData = {
      ...voucherDetails,
      docno: finalDocNo,
      acdebit: selectedDebitAccount?.ps_Id || voucherDetails.acdebit,
      accredit: selectedCreditAccount?.ps_Id || voucherDetails.accredit,
      bankname: selectedBank?.bankname || voucherDetails.bankname,
      docmode: selectedBankPayTp?.paytype || voucherDetails.docmode,
      MR_paymentmode: paymentMode,
    };

    try {
      let savedVoucher;
      if (id) {
        savedVoucher = await updateMutateAsync({ id, data: formData });
      } else {
        savedVoucher = await createMutateAsync(formData);
      }

      const savedDocumentNumber = savedVoucher?.docno || formData.docno;
      if (attachment && savedDocumentNumber) {
        await uploadDocument({
          file: attachment,
          filename: `MR-${savedDocumentNumber}`,
        });
      }

      if (cndnNoteType && !id) {
        setShowRedirectOverlay(true);
        const params = new URLSearchParams({
          fromMR: "true",
          noteType: cndnNoteType,
          vendorId: selectedDebitAccount?.ps_Id || formData.acdebit || "",
          vendorName: selectedDebitAccount?.name || "",
          customerId: selectedCreditAccount?.ps_Id || formData.accredit || "",
          customerName: selectedCreditAccount?.name || "",
          date: formData.trdate || "",
          remark: formData.naration || "",
        });
        setTimeout(() => {
          onClose();
          navigate(`/cndn/new?${params.toString()}`);
        }, 2000);
      } else {
        onClose();
      }
    } catch (error) {
      console.error("Error submitting voucher:", error);
    }
  };

  // Set the receipt number preview for new vouchers.
  useEffect(() => {
    if (!id && voucherNumberData?.nextVoucherNumber) {
      setVoucherDetails(prev => ({
        ...prev,
        docno: voucherNumberData.nextVoucherNumber
      }));
    }
  }, [voucherNumberData, id]);

  // Load voucher data for edit mode
  useEffect(() => {
    if (data && id) {
      setVoucherDetails({
        docno: data.docno || "",
        trdate: data.trdate || "",
        trtyp: "MR",
        acdebit: data.acdebit || "",
        accredit: data.accredit || "",
        tramount: data.tramount || "0.00",
        naration: data.naration || "",
        bankname: data.bankname || "",
        docmode: data.docmode || "",
        trnid: data.trnid || "",
        trndt: data.trndt || "",
        is_revised: data.is_revised || 0,
      });

      // Detect payment mode from prpdr field
      if (data.prpdr === "CASHAC") {
        setPaymentMode("C");
      } else {
        setPaymentMode("B");
      }

      // Set selected accounts for edit mode
      if (data.debitAccount) {
        setSelectedDebitAccount(data.debitAccount);
      }
      if (data.creditAccount) {
        setSelectedCreditAccount(data.creditAccount);
      }
      if (data.bankname && allBanks) {
        const bank = allBanks.find((b) => b.bankname === data.bankname);
        if (bank) setSelectedBank(bank);
      }
      if (data.docmode && allBankPayTps) {
        const payType = allBankPayTps.find((p) => p.paytype === data.docmode);
        if (payType) setSelectedBankPayTp(payType);
      }
    }
  }, [data, allBanks, allBankPayTps, id]);

  const isPending = isCreating || isUpdating || isUploading;
  const isReconciled = data?.adj === 1;

  const paymentModeOptions = [
    { value: "B", label: "Bank" },
    { value: "C", label: "Cash" },
  ];

  return (
    <>
      {showRedirectOverlay && <RedirectOverlay noteType={cndnNoteType} />}
      <Modal
        open={open}
        setOpen={(val) => !val && onClose()}
        maxWidth="4xl"
        header={
          <h3 className="text-base font-semibold leading-6 text-gray-900">
            {id ? "Update" : "Add"} Money Receipt
          </h3>
        }
        footer={
          <div className="flex items-center justify-end gap-x-3">
            <button
              type="button"
              className="rounded-md px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 shadow-sm hover:bg-gray-50 disabled:opacity-50"
              onClick={onClose}
              disabled={isPending}
            >
              Cancel
            </button>
            {!isReconciled && (
              <button
                type="submit"
                form="set-money-receipt-form"
                disabled={isPending}
                className="rounded-md bg-gray-800 px-5 py-2 text-sm font-medium text-white shadow-sm hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isPending
                  ? id ? "Updating..." : "Creating..."
                  : id ? "Update" : "Create"}
              </button>
            )}
          </div>
        }
      >
      {id && isLoading ? (
        <FormSkeleton />
      ) : (
      <form id="set-money-receipt-form" onSubmit={handleSubmit}>
        {id && isReconciled && (
          <div className="mb-6 rounded-md bg-red-50 border border-red-200 px-4 py-3" role="alert">
            <p className="text-sm font-semibold text-red-700">Document cannot be edited. Already Reconciled.</p>
          </div>
        )}

        {/* Receipt Info */}
        <div className="mb-5">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <Input
                type="text"
                name="docno"
                label="Voucher No."
                value={voucherDetails.docno}
                placeholder="Auto-generated"
                readOnly
              />
              {!id && (
                <p className="mt-1 text-xs text-gray-500">
                  {voucherNumberData?.previousVoucherNumber
                    ? `Previous Money Receipt No.: ${voucherNumberData.previousVoucherNumber}`
                    : "No previous Money Receipt voucher in this financial year."}
                </p>
              )}
              <div className="mt-2">
                <Input
                  type="checkbox"
                  label="Custom MR"
                  value={showCustomMR}
                  onChange={(e) => setShowCustomMR(e.target.checked)}
                />
              </div>
              {showCustomMR && (
                <div className="mt-2">
                  <Input
                    type="text"
                    name="custom_mrno"
                    label="Custom MR No."
                    value={voucherDetails.custom_mrno}
                    onChange={handleInputChange}
                    placeholder="Custom MR No."
                    maxLength={15}
                  />
                </div>
              )}
            </div>
            <Input
              type="date"
              name="trdate"
              label="Date"
              value={voucherDetails.trdate}
              onChange={handleInputChange}
              now={!id}
              withinFY={true}
              isRequired={true}
            />
            <Select
              label="Mode of Payment"
              value={paymentModeOptions.find((o) => o.value === paymentMode)}
              onChange={(opt) => setPaymentMode(opt?.value || "B")}
              options={paymentModeOptions}
              isSearchable={false}
              isClearable={false}
              isRequired={true}
            />
          </div>
        </div>

        {/* Particulars & Amount */}
        <div className="mb-5">
          <div className="sm:flex">
            <div className="sm:w-2/3 sm:border-r sm:border-gray-200 sm:pr-6">
              <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-3">Particulars</h3>

              <div className="mb-4">
                <SelectVendors
                  value={selectedDebitAccount}
                  onChange={setSelectedDebitAccount}
                  label="Debit A/c (Vendor)"
                  placeholder="Select here"
                  isRequired={true}
                />
              </div>

              <div className="ml-10 mb-4">
                <SelectCustomers
                  value={selectedCreditAccount}
                  onChange={setSelectedCreditAccount}
                  label="Credit A/c (Customer)"
                  placeholder="Select here"
                  isRequired={true}
                />
              </div>

              <fieldset className="mb-4">
                <legend className="text-sm font-medium text-gray-700 mb-2">Type</legend>
                <div className="flex items-center gap-6">
                  <label htmlFor="onAccount" className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                    <input
                      id="onAccount"
                      name="selectionType"
                      type="radio"
                      checked={selectionType === "ACC"}
                      onChange={() => handleSelectionTypeChange("ACC")}
                      className="h-4 w-4 border-gray-300 text-gray-800 focus:ring-gray-600"
                    />
                    On Account
                  </label>
                  <label htmlFor="againstbill" className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer">
                    <input
                      id="againstbill"
                      name="selectionType"
                      type="radio"
                      checked={selectionType === "BILL"}
                      onChange={() => handleSelectionTypeChange("BILL")}
                      className="h-4 w-4 border-gray-300 text-gray-800 focus:ring-gray-600"
                    />
                    Against Bill
                  </label>
                </div>
              </fieldset>

              <TextArea
                name="naration"
                label="Narration"
                value={voucherDetails.naration}
                onChange={handleInputChange}
                placeholder="Narration"
                rows={3}
                maxLength={255}
              />
            </div>

            <div className="sm:w-1/3 sm:pl-6 mt-4 sm:mt-0">
              <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-3">Amount</h3>
              <Input
                type="number"
                name="tramount"
                value={voucherDetails.tramount}
                onChange={handleInputChange}
                placeholder="0.00"
                step="0.01"
                showLabel={false}
                isRequired={true}
              />

              {!id && (
                <fieldset className="mt-4">
                  <legend className="text-sm font-medium text-gray-700 mb-2">Create Note</legend>
                  <div className="flex flex-col gap-2">
                    {cndnNoteOptions.map((opt) => (
                      <label
                        key={opt.value}
                        className="flex items-center gap-2 text-sm text-gray-900 cursor-pointer"
                      >
                        <input
                          type="radio"
                          name="cndnNoteType"
                          value={opt.value}
                          checked={cndnNoteType === opt.value}
                          onChange={() => setCndnNoteType(opt.value)}
                          className="h-4 w-4 border-gray-300 text-gray-800 focus:ring-gray-600"
                        />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </fieldset>
              )}
            </div>
          </div>
        </div>

        {/* Bank Details */}
        {paymentMode === "B" && (
          <div className="mb-5">
            <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide mb-3">Bank Details</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              <Select
                value={selectedBank}
                onChange={setSelectedBank}
                options={allBanks}
                getOptionValue={(option) => option?.bnkid}
                getOptionLabel={(option) => option?.bankname}
                label="Name of Bank"
                placeholder="Select here"
                isLoading={isLoadingAllBanks}
                isClearable={true}
                isSearchable={true}
                isRequired={true}
              />
              <Select
                value={selectedBankPayTp}
                onChange={setSelectedBankPayTp}
                options={allBankPayTps}
                getOptionValue={(option) => option?.paytyp}
                getOptionLabel={(option) => option?.paytype}
                label="Payment Mode"
                placeholder="Select here"
                isLoading={isLoadingAllBankPayTps}
                isClearable={true}
                isSearchable={true}
                isRequired={true}
              />
              <Input
                type="text"
                name="trnid"
                label="Chq/DD/TRF No."
                value={voucherDetails.trnid}
                onChange={handleInputChange}
                placeholder="Transaction No"
                maxLength={25}
                isRequired={true}
              />
              <Input
                type="date"
                name="trndt"
                label="Chq/DD/TRF Date"
                value={voucherDetails.trndt}
                onChange={handleInputChange}
                withinFY={true}
                isRequired={true}
              />
            </div>
          </div>
        )}

        {isDocUploaderEnabled() && docFilename && (
          <div className="mb-5">
            <FileUploader
              docFilename={id ? docFilename : ""}
              label="Attachment"
              deferred
              selectedFile={attachment}
              onFileSelected={setAttachment}
            />
          </div>
        )}

        {/* Revise Money Receipt */}
        {id && (
          <div className="mb-5">
            <Input
              type="checkbox"
              label="Revise Money Receipt"
              value={voucherDetails.is_revised === 1}
              onChange={(e) => setVoucherDetails(prev => ({
                ...prev,
                is_revised: e.target.checked ? 1 : 0
              }))}
              disabled={isReconciled}
            />
          </div>
        )}

        {/* Audit Info */}
        {id && data && (
          <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-400">
            <p>
              Created by {data.usrid} on{" "}
              {data.datecreate &&
                new Date(data.datecreate).toLocaleString("en-IN", {
                  day: "2-digit", month: "2-digit", year: "numeric",
                  hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
                })}
            </p>
            {data.modusrid && data.modusrid.length > 0 && (
              <p>
                Edited by {data.modusrid} on{" "}
                {data.moddt &&
                  new Date(data.moddt).toLocaleString("en-IN", {
                    day: "2-digit", month: "2-digit", year: "numeric",
                    hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
                  })}
              </p>
            )}
          </div>
        )}
      </form>
      )}

      <OutstandingBillsModal
        isOpen={showBillsModal}
        onClose={() => setShowBillsModal(false)}
        vendorId={selectedDebitAccount?.ps_Id}
        customerId={selectedCreditAccount?.ps_Id}
        onBillsSelected={handleBillsSelected}
      />
      </Modal>
    </>
  );
}

